import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Mic, Square, Loader2, X, RotateCcw, Check, Video } from 'lucide-react';

interface Props {
  /** What to capture: video+audio for training/consent, audio-only for voice clone */
  mode: 'video' | 'audio';
  /** Suggested duration in seconds — shown as guidance, doesn't auto-stop */
  hintSeconds?: { min: number; max: number };
  /** Optional teleprompter script — overlaid on the preview so the speaker
   *  can read it while recording. Used for consent recording mainly. */
  scriptText?: string;
  /** Modal title override */
  title?: string;
  onCancel: () => void;
  onCapture: (file: File) => void;
}

/**
 * In-browser MediaRecorder panel — lets the user record video or audio without
 * leaving the modal. Output is a webm Blob wrapped in a File, ready to upload.
 *
 * Records up to a soft cap (15min for video, 10min for audio) to avoid runaway.
 * Safari users may need to grant camera permission per-site.
 */
const MediaRecorderPanel: React.FC<Props> = ({ mode, hintSeconds, scriptText, title, onCancel, onCapture }) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const [permState, setPermState] = useState<'idle' | 'asking' | 'ready' | 'recording' | 'recorded' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  // Live mic visualizer — drawn into a canvas via Web Audio API
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);

  const SOFT_CAP = mode === 'video' ? 15 * 60 : 10 * 60;

  // Initialize permissions + preview stream on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPermState('asking');
      try {
        const constraints = mode === 'video'
          ? { video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }, audio: true }
          : { audio: true, video: false };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        // Don't try to set srcObject here — the <video> element isn't rendered
        // during 'asking'. The next useEffect attaches it once permState='ready'.
        setPermState('ready');
      } catch (e: any) {
        setPermState('error');
        setError(
          e?.name === 'NotAllowedError'
            ? (t('videoStudio.recorder.permDenied', 'Permissão negada. Libere a câmera/microfone nas configurações do navegador.') as string)
            : (t('videoStudio.recorder.permFailed', 'Não conseguiu acessar a câmera/microfone: {{msg}}', { msg: e?.message || 'erro' }) as string)
        );
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach(track => track.stop());
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Wire the live stream into the <video> element AFTER it renders.
  // Re-runs when permState transitions to 'ready' or 'recording' so the preview
  // re-attaches if the video element was unmounted (e.g. switching from 'recorded' back via retake).
  useEffect(() => {
    if (mode !== 'video') return;
    if (permState !== 'ready' && permState !== 'recording') return;
    const stream = streamRef.current;
    const el = videoRef.current;
    if (stream && el && el.srcObject !== stream) {
      el.srcObject = stream;
      el.play().catch(() => {});
    }
  }, [permState, mode]);

  // Audio visualizer — connects the mic stream to an AnalyserNode and draws
  // live frequency bars into the canvas. Gives the user real visual proof
  // that the mic is picking up sound.
  useEffect(() => {
    if (permState !== 'ready' && permState !== 'recording') return;
    const stream = streamRef.current;
    const canvas = canvasRef.current;
    if (!stream || !canvas) return;

    const Ctor: any = (window as any).AudioContext || (window as any).webkitAudioContext;
    const audioCtx: AudioContext = new Ctor();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    audioCtxRef.current = audioCtx;
    analyserRef.current = analyser;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const buffer = new Uint8Array(analyser.frequencyBinCount);

    const draw = () => {
      analyser.getByteFrequencyData(buffer);
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      const bars = 48;
      const step = Math.floor(buffer.length / bars);
      const barWidth = (w / bars) * 0.7;
      const gap = (w / bars) * 0.3;
      // Average level → mic indicator color
      const avg = buffer.reduce((a, b) => a + b, 0) / buffer.length;
      const isHot = avg > 50;
      for (let i = 0; i < bars; i++) {
        const v = buffer[i * step] / 255;
        const barH = Math.max(2, v * h * 0.95);
        const x = i * (barWidth + gap);
        const y = (h - barH) / 2;
        // Gradient: violet at low, fuchsia at mid, amber at peak
        const grad = ctx.createLinearGradient(0, y, 0, y + barH);
        if (v > 0.7)      { grad.addColorStop(0, '#fbbf24'); grad.addColorStop(1, '#f59e0b'); }
        else if (v > 0.35){ grad.addColorStop(0, '#ec4899'); grad.addColorStop(1, '#a855f7'); }
        else              { grad.addColorStop(0, '#a78bfa'); grad.addColorStop(1, '#7c3aed'); }
        ctx.fillStyle = grad;
        ctx.fillRect(x, y, barWidth, barH);
      }
      // Overall sound floor indicator (small dot bottom-right)
      ctx.fillStyle = isHot ? '#10b981' : '#6b7280';
      ctx.beginPath();
      ctx.arc(w - 12, h - 12, 4, 0, Math.PI * 2);
      ctx.fill();
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { source.disconnect(); } catch {}
      try { analyser.disconnect(); } catch {}
      try { audioCtx.close(); } catch {}
      audioCtxRef.current = null;
      analyserRef.current = null;
    };
  }, [permState]);

  // Soft cap timer + elapsed counter
  useEffect(() => {
    if (permState !== 'recording') return;
    const interval = setInterval(() => {
      setElapsed(s => {
        if (s + 1 >= SOFT_CAP) { stopRecording(); return s + 1; }
        return s + 1;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permState]);

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    setElapsed(0);
    // Pick the best mime the browser supports for our mode
    const candidates = mode === 'video'
      ? ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']
      : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
    const mimeType = candidates.find(m => MediaRecorder.isTypeSupported(m)) || candidates[candidates.length - 1];
    const recorder = new MediaRecorder(streamRef.current, { mimeType });
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType.split(';')[0] });
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
      setRecordedBlob(blob);
      setRecordedUrl(URL.createObjectURL(blob));
      setPermState('recorded');
    };
    recorder.start(1000);
    recorderRef.current = recorder;
    setPermState('recording');
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
  };

  const retake = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setElapsed(0);
    setPermState('ready');
  };

  const useRecording = () => {
    if (!recordedBlob) return;
    const ext = mode === 'video' ? 'webm' : 'webm';
    const filename = mode === 'video' ? `recorded-${Date.now()}.${ext}` : `voice-${Date.now()}.${ext}`;
    const file = new File([recordedBlob], filename, { type: recordedBlob.type });
    onCapture(file);
  };

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const inGoodRange = hintSeconds ? (elapsed >= hintSeconds.min && elapsed <= hintSeconds.max) : true;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-gray-950/85 backdrop-blur-sm" onClick={permState === 'recording' ? undefined : onCancel}>
      <div className="w-full max-w-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
            {mode === 'video' ? <Video className="w-4 h-4 text-rose-500" /> : <Mic className="w-4 h-4 text-violet-500" />}
            {title || (mode === 'video'
              ? t('videoStudio.recorder.titleVideo', 'Gravar vídeo')
              : t('videoStudio.recorder.titleAudio', 'Gravar áudio'))}
          </h3>
          <button onClick={onCancel} disabled={permState === 'recording'} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400 disabled:opacity-50">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          {permState === 'asking' && (
            <div className="aspect-video bg-gray-100 dark:bg-gray-950 rounded-lg flex flex-col items-center justify-center gap-2 text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p className="text-xs">{t('videoStudio.recorder.requestingPermission', 'Solicitando permissão de câmera/microfone…')}</p>
            </div>
          )}

          {permState === 'error' && (
            <div className="aspect-video bg-rose-50 dark:bg-rose-900/20 rounded-lg flex flex-col items-center justify-center gap-2 p-4 text-center">
              <X className="w-8 h-8 text-rose-500" />
              <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">{error}</p>
              <button onClick={onCancel} className="mt-2 px-3 py-1.5 rounded bg-rose-500 text-white text-xs font-semibold">
                {t('common.close')}
              </button>
            </div>
          )}

          {/* Live preview / recorded preview */}
          {(permState === 'ready' || permState === 'recording') && (
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
              {mode === 'video' ? (
                <video ref={videoRef} muted autoPlay playsInline className="w-full h-full object-contain" />
              ) : (
                <div className="absolute inset-0 flex flex-col">
                  {/* Audio mode — when there's a teleprompter script, give it most of
                      the space (user needs to read while recording). Otherwise centered mic. */}
                  {scriptText ? (
                    <div className="flex-1 overflow-y-auto px-4 py-3 text-white text-sm leading-relaxed font-medium whitespace-pre-line bg-gray-950/30">
                      <p className="text-[10px] uppercase tracking-wider text-violet-300 font-semibold mb-2 sticky top-0 bg-gray-950/80 backdrop-blur-sm py-1 -mx-4 px-4">
                        {t('videoStudio.recorder.teleprompter', 'Leia em voz alta:')}
                      </p>
                      {scriptText}
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center">
                      <Mic className={`w-12 h-12 ${permState === 'recording' ? 'text-rose-500 animate-pulse' : 'text-violet-400'}`} />
                    </div>
                  )}
                  {/* Live waveform — proves the mic is picking up sound */}
                  <div className="px-4 shrink-0">
                    <canvas
                      ref={canvasRef}
                      width={640}
                      height={80}
                      className="w-full h-16 rounded-lg bg-gray-950/40 border border-violet-500/20"
                    />
                    <p className="text-[10px] text-gray-400 text-center mt-2">
                      {permState === 'recording'
                        ? t('videoStudio.recorder.recordingAudio', 'Gravando áudio…')
                        : t('videoStudio.recorder.speakNow', 'Fale alguma coisa pra testar — você deve ver as ondas mexerem')}
                    </p>
                  </div>
                  <div className="h-2" />
                </div>
              )}
              {permState === 'recording' && (
                <div className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-rose-600 text-white text-xs font-bold">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  REC
                </div>
              )}
              {/* Time badge — always visible (shows max even before recording starts).
                  Color: gray pre-record, green when in good range, amber outside. */}
              {hintSeconds && (
                <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-mono font-bold ${
                  permState !== 'recording'
                    ? 'bg-gray-700 text-white'
                    : inGoodRange ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'
                }`}>
                  {fmt(elapsed)}
                  <span className="ml-1 opacity-70 text-[10px]">/ {fmt(hintSeconds.max)}</span>
                </div>
              )}

              {/* Teleprompter overlay — VIDEO mode only. Audio mode renders the
                  teleprompter inside the audio block above (full-area scrollable),
                  so we'd duplicate the text if we showed it here too. */}
              {scriptText && mode === 'video' && (
                <div className="absolute inset-x-0 bottom-0 max-h-[55%] flex flex-col bg-gradient-to-t from-black/95 via-black/85 to-black/40 backdrop-blur-sm">
                  <p className="text-[10px] uppercase tracking-wider text-white/70 font-semibold px-3 pt-2 pb-1 shrink-0">
                    {t('videoStudio.recorder.teleprompter', 'Leia em voz alta:')}
                  </p>
                  <div className="overflow-y-auto px-3 pb-3 text-white text-sm leading-relaxed font-medium whitespace-pre-line">
                    {scriptText}
                  </div>
                </div>
              )}
            </div>
          )}

          {permState === 'recorded' && recordedUrl && (
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
              {mode === 'video' ? (
                <video src={recordedUrl} controls className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <audio src={recordedUrl} controls className="w-3/4" />
                </div>
              )}
              <span className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-mono font-bold bg-emerald-600 text-white">
                {fmt(elapsed)}
              </span>
            </div>
          )}

          {/* Hint */}
          {hintSeconds && permState !== 'error' && (
            <p className="text-[11px] text-gray-500 text-center">
              {t('videoStudio.recorder.targetRange', 'Recomendado: {{min}}–{{max}}', { min: fmt(hintSeconds.min), max: fmt(hintSeconds.max) })}
            </p>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-2">
          {permState === 'ready' && (
            <>
              <button onClick={onCancel} className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300">
                {t('common.close')}
              </button>
              <button onClick={startRecording} className="px-4 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold inline-flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-white" />
                {t('videoStudio.recorder.startRecording', 'Iniciar gravação')}
              </button>
            </>
          )}
          {permState === 'recording' && (
            <button onClick={stopRecording} className="px-4 py-2 rounded-lg bg-gray-900 hover:bg-gray-700 text-white text-sm font-semibold inline-flex items-center gap-2">
              <Square className="w-3.5 h-3.5 fill-white" />
              {t('videoStudio.recorder.stopRecording', 'Parar e revisar')}
            </button>
          )}
          {permState === 'recorded' && (
            <>
              <button onClick={retake} className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300 inline-flex items-center gap-1.5">
                <RotateCcw className="w-3.5 h-3.5" /> {t('videoStudio.recorder.retake', 'Regravar')}
              </button>
              <button onClick={useRecording} className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold inline-flex items-center gap-2">
                <Check className="w-4 h-4" /> {t('videoStudio.recorder.useRecording', 'Usar essa gravação')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaRecorderPanel;
